// The Sales portal's demo data source: realistic, in memory, reset on reload.
// Used whenever the portal has no Supabase configured (shouldUseSupabase()),
// so the screens can be reviewed without reaching any real organisation.
//
// It keeps the server's rules where a reviewer would notice them: the role
// checks of app_platform_can, "say why it was lost", the last owner, a month
// that has not ended, and an audit entry for every change.

import { PortalWriteError } from "./supabaseAdapter";
import type { Invoice, InvoiceStatus, OrgKind, OrgLifecycle } from "./types";
import type {
  AccountFields,
  AgeBucket,
  AuditEntry,
  BillingPreviewRow,
  Onboarding,
  Prospect,
  ProspectFields,
  Receivable,
  SalesAccessCode,
  SalesAccount,
  SalesAction,
  SalesDataSource,
  SalesReports,
  SalesRole,
  TeamMember,
} from "./sales";

const DAY = 86_400_000;

// ---- The demo viewer's role (switchable in the demo only) ---------------------

let demoRole: SalesRole = "owner";
export function getDemoSalesRole(): SalesRole {
  return demoRole;
}
export function setDemoSalesRole(role: SalesRole): void {
  demoRole = role;
}

const CAN: Record<SalesRole, readonly SalesAction[]> = {
  owner: ["accounts", "pipeline", "codes", "agreements", "org_status", "invoices", "billing", "team"],
  sales: ["accounts", "pipeline", "codes", "agreements", "org_status"],
  finance: ["invoices", "billing", "org_status"],
  support: [],
};

function must(action: SalesAction): void {
  if (!CAN[demoRole].includes(action)) {
    throw new PortalWriteError("demo", "not authorized: your Zooylo role cannot do this", "42501");
  }
}

// ---- Helpers --------------------------------------------------------------------

const iso = (ms: number) => new Date(ms).toISOString();
const isoDate = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const round2 = (n: number) => Math.round(n * 100) / 100;
let seq = 100;
const newId = (p: string) => `${p}-${(seq++).toString(36)}`;

function monthBounds(month: string): { start: string; end: string; endMs: number } {
  const [y, m] = month.slice(0, 7).split("-").map(Number);
  const start = Date.UTC(y, m - 1, 1);
  const endMs = Date.UTC(y, m, 1) - DAY;
  return { start: isoDate(start), end: isoDate(endMs), endMs };
}

function bucketFor(days: number, dueInFuture: boolean): AgeBucket {
  if (dueInFuture || days <= 0) return "current";
  if (days <= 30) return "1-30";
  if (days <= 60) return "31-60";
  if (days <= 90) return "61-90";
  return "90+";
}

// ---- Seed -------------------------------------------------------------------------

interface DemoState {
  accounts: SalesAccount[];
  prospects: Prospect[];
  invoices: Invoice[];
  codes: SalesAccessCode[];
  team: TeamMember[];
  audit: (AuditEntry & { orgId: string | null })[];
  onboarding: Record<string, Onboarding>;
}

function account(p: Partial<SalesAccount> & Pick<SalesAccount, "orgId" | "name">): SalesAccount {
  return {
    kind: "organisation",
    status: "active",
    createdAt: iso(Date.now() - 200 * DAY),
    legalName: null,
    registrationNo: null,
    country: "MY",
    billingAddress: null,
    taxId: null,
    contactName: null,
    contactEmail: null,
    contactPhone: null,
    salesOwner: null,
    source: null,
    notes: null,
    adminEmails: [],
    familyAllowance: 0,
    licencesLive: 0,
    licencesClaimed: 0,
    familyPriceMonthly: 30,
    familyRetailPriceMonthly: 40,
    sstRate: 8,
    currency: "MYR",
    paymentTermsDays: 30,
    agreementNotes: null,
    contractStart: null,
    contractEnd: null,
    billingEmail: null,
    outstanding: 0,
    ...p,
  };
}

function seed(now: number): DemoState {
  const ago = (d: number) => iso(now - d * DAY);
  const agoDate = (d: number) => isoDate(now - d * DAY);
  const ahead = (d: number) => isoDate(now + d * DAY);

  const accounts: SalesAccount[] = [
    account({
      orgId: "org-willow",
      name: "Willow Learning Centre",
      createdAt: ago(240),
      legalName: "Willow Learning Centre Sdn. Bhd.",
      registrationNo: "202301004512 (1498823-K)",
      billingAddress: "12 Jalan Bangsar Utama 3, 59000 Kuala Lumpur",
      taxId: "W10-2304-32000117",
      contactName: "Mei Ling Tan",
      contactEmail: "meiling@willowcentre.my",
      contactPhone: "+60 12-345 6789",
      salesOwner: "Hafiz",
      source: "Referral — Bright Steps",
      adminEmails: ["meiling@willowcentre.my"],
      familyAllowance: 20,
      licencesLive: 14,
      licencesClaimed: 12,
      contractStart: agoDate(240),
      contractEnd: ahead(125),
      billingEmail: "accounts@willowcentre.my",
    }),
    account({
      orgId: "org-starsewa",
      name: "StarSewa",
      createdAt: ago(180),
      legalName: "Persatuan StarSewa Malaysia",
      registrationNo: "PPM-001-14-02032019",
      billingAddress: "8 Lorong Damai, 50400 Kuala Lumpur",
      contactName: "Ravi Subramaniam",
      contactEmail: "ravi@starsewa.org",
      contactPhone: "+60 17-220 4410",
      salesOwner: "Awais",
      source: "Partner NGO",
      notes: "Free partnership. Families are sponsored by StarSewa; no invoice.",
      adminEmails: ["ravi@starsewa.org", "ops@starsewa.org"],
      familyAllowance: 50,
      licencesLive: 30,
      licencesClaimed: 22,
      familyPriceMonthly: 0,
      familyRetailPriceMonthly: 0,
      sstRate: 0,
      agreementNotes: "Pilot partnership, reviewed every six months.",
      contractStart: agoDate(180),
      contractEnd: ahead(185),
      billingEmail: "ravi@starsewa.org",
    }),
    account({
      orgId: "org-brightsteps",
      name: "Bright Steps Therapy Clinic",
      createdAt: ago(320),
      legalName: "Bright Steps Therapy Sdn. Bhd.",
      registrationNo: "201901022871 (1331002-W)",
      billingAddress: "33 Lebuh Pantai, 10300 George Town, Pulau Pinang",
      taxId: "W10-1909-32001520",
      contactName: "Dr. Siti Nurhaliza",
      contactEmail: "siti@brightsteps.my",
      contactPhone: "+60 4-261 8800",
      salesOwner: "Hafiz",
      source: "Conference — OT Malaysia",
      adminEmails: ["siti@brightsteps.my"],
      familyAllowance: 15,
      licencesLive: 15,
      licencesClaimed: 15,
      familyPriceMonthly: 28,
      paymentTermsDays: 14,
      agreementNotes: "Volume price agreed: RM28 per licence.",
      contractStart: agoDate(320),
      contractEnd: ahead(45),
      billingEmail: "finance@brightsteps.my",
    }),
    account({
      orgId: "org-aina",
      name: "Dr. Aina Rahman",
      kind: "individual",
      createdAt: ago(95),
      contactName: "Aina Rahman",
      contactEmail: "aina.rahman.slp@gmail.com",
      contactPhone: "+60 13-998 1203",
      salesOwner: "Awais",
      source: "Website",
      adminEmails: ["aina.rahman.slp@gmail.com"],
      familyAllowance: 5,
      licencesLive: 3,
      licencesClaimed: 3,
      familyRetailPriceMonthly: 45,
      contractStart: agoDate(95),
      contractEnd: ahead(270),
      billingEmail: "aina.rahman.slp@gmail.com",
    }),
    account({
      orgId: "org-sunrise",
      name: "Sunrise Inclusive School",
      status: "suspended",
      createdAt: ago(300),
      legalName: "Sunrise Education Sdn. Bhd.",
      billingAddress: "5 Jalan Harmonium, 81100 Johor Bahru",
      contactName: "Kevin Lim",
      contactEmail: "kevin@sunrise.edu.my",
      salesOwner: "Hafiz",
      source: "Cold outreach",
      notes: "Suspended for non-payment. Two reminders sent.",
      adminEmails: ["kevin@sunrise.edu.my"],
      familyAllowance: 25,
      licencesLive: 10,
      licencesClaimed: 8,
      contractStart: agoDate(300),
      contractEnd: ahead(65),
      billingEmail: "bursar@sunrise.edu.my",
    }),
    account({
      orgId: "org-harbour",
      name: "Little Harbour Speech",
      status: "closed",
      createdAt: ago(400),
      contactName: "Nora Abdullah",
      contactEmail: "nora@littleharbour.my",
      salesOwner: "Awais",
      source: "Website",
      notes: "Closed at the end of their contract; the practice moved abroad.",
      adminEmails: ["nora@littleharbour.my"],
      familyAllowance: 10,
      contractStart: agoDate(400),
      contractEnd: agoDate(35),
    }),
    account({
      orgId: "org-kiddo",
      name: "Kiddo Hub Early Intervention",
      createdAt: ago(9),
      contactName: "Farah Iskandar",
      contactEmail: "farah@kiddohub.my",
      contactPhone: "+60 11-1234 5678",
      salesOwner: "Hafiz",
      source: "Pipeline — demo day",
      adminEmails: ["farah@kiddohub.my"],
      familyAllowance: 12,
      licencesLive: 2,
      licencesClaimed: 0,
      contractStart: agoDate(9),
      contractEnd: ahead(356),
    }),
  ];

  let invNo = 1;
  const invoice = (orgId: string, periodMonthsAgo: number, lm: number, price: number, rate: number, status: InvoiceStatus, issuedDaysAgo: number, termsDays: number, paidDaysAgo?: number): Invoice => {
    const d = new Date(now);
    const ps = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - periodMonthsAgo, 1);
    const pe = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - periodMonthsAgo + 1, 1) - DAY;
    const subtotal = round2(lm * price);
    const sst = round2((subtotal * rate) / 100);
    return {
      id: `inv-${invNo}`,
      orgId,
      number: `INV-${new Date(now - issuedDaysAgo * DAY).toISOString().slice(0, 7).replace("-", "")}-D${String(invNo++).padStart(3, "0")}`,
      periodStart: isoDate(ps),
      periodEnd: isoDate(pe),
      licenceMonths: lm,
      unitPrice: price,
      subtotal,
      sstRate: rate,
      sstAmount: sst,
      amount: round2(subtotal + sst),
      currency: "MYR",
      status,
      issuedAt: ago(issuedDaysAgo),
      dueAt: agoDate(issuedDaysAgo - termsDays),
      paidAt: paidDaysAgo == null ? null : ago(paidDaysAgo),
      note: null,
    };
  };

  const invoices: Invoice[] = [
    // Willow: paid history, one current, one 1–30 days late.
    invoice("org-willow", 4, 10.5, 30, 8, "paid", 110, 30, 85),
    invoice("org-willow", 3, 11.2, 30, 8, "paid", 80, 30, 52),
    invoice("org-willow", 2, 11.8, 30, 8, "issued", 50, 30),
    invoice("org-willow", 1, 12, 30, 8, "issued", 12, 30),
    // Bright Steps: pays on 14 days, one 31–60 late.
    invoice("org-brightsteps", 3, 14.6, 28, 8, "paid", 80, 14, 70),
    invoice("org-brightsteps", 2, 15, 28, 8, "issued", 60, 14),
    // Aina: small and current.
    invoice("org-aina", 1, 3, 30, 8, "issued", 8, 30),
    invoice("org-aina", 2, 2.4, 30, 8, "paid", 40, 30, 20),
    // Sunrise: 61–90 and 90+ — why it is suspended.
    invoice("org-sunrise", 4, 8, 30, 8, "issued", 125, 30),
    invoice("org-sunrise", 3, 8, 30, 8, "issued", 100, 30),
    invoice("org-sunrise", 5, 9, 30, 8, "void", 150, 30),
  ];

  for (const a of accounts) {
    a.outstanding = round2(invoices.filter((i) => i.orgId === a.orgId && i.status === "issued").reduce((s, i) => s + i.amount, 0));
  }

  const prospect = (p: Partial<Prospect> & Pick<Prospect, "id" | "name" | "stage">): Prospect => ({
    kind: "organisation",
    contactName: null,
    contactEmail: null,
    contactPhone: null,
    expectedLicences: null,
    nextAction: null,
    nextActionOn: null,
    lostReason: null,
    notes: null,
    orgId: null,
    createdAt: ago(30),
    updatedAt: ago(3),
    ...p,
  });

  const prospects: Prospect[] = [
    prospect({ id: "pr-1", name: "Taman Ceria Kindergarten", stage: "lead", contactName: "Puan Rohana", contactEmail: "rohana@tamanceria.my", expectedLicences: 8, nextAction: "First call", nextActionOn: ahead(2), notes: "Found us on Instagram." }),
    prospect({ id: "pr-2", name: "Autism Link Kuantan", stage: "lead", contactName: "Jason Wong", expectedLicences: 20, nextAction: "Send the one-page overview", nextActionOn: ahead(5) }),
    prospect({ id: "pr-3", name: "Pusat Pemulihan Seri Kembangan", stage: "qualified", contactName: "Encik Zul", contactEmail: "zul@ppsk.org.my", expectedLicences: 30, nextAction: "Book a demo", nextActionOn: ahead(1) }),
    prospect({ id: "pr-4", name: "Hana Lee (speech therapist)", kind: "individual", stage: "demo", contactName: "Hana Lee", contactEmail: "hana.slt@outlook.com", expectedLicences: 6, nextAction: "Demo on video call", nextActionOn: ahead(3) }),
    prospect({ id: "pr-5", name: "Rainbow Bridge Learning", stage: "proposal", contactName: "Priya Nair", contactEmail: "priya@rainbowbridge.my", expectedLicences: 18, nextAction: "Follow up on the proposal", nextActionOn: agoDate(1), notes: "Asked about the SST line." }),
    prospect({ id: "pr-6", name: "Klinik Tumbesaran Anak", stage: "negotiating", contactName: "Dr. Ahmad Faizal", expectedLicences: 40, nextAction: "Agree the volume price", nextActionOn: ahead(7) }),
    prospect({ id: "pr-7", name: "Little Explorers Ipoh", stage: "won", contactName: "Grace Teoh", contactEmail: "grace@littleexplorers.my", expectedLicences: 10, nextAction: "Send the access code", nextActionOn: ahead(0) }),
    prospect({ id: "pr-8", name: "Kiddo Hub Early Intervention", stage: "won", contactName: "Farah Iskandar", expectedLicences: 12, orgId: "org-kiddo", updatedAt: ago(9) }),
    prospect({ id: "pr-9", name: "Sri Murni Daycare", stage: "lost", contactName: "Puan Salmah", expectedLicences: 6, lostReason: "Chose to wait until next year's budget." }),
  ];

  const codes: SalesAccessCode[] = [
    { code: "ZOO-7F3A91C2", label: "Kiddo Hub Early Intervention", kind: "organisation", seatsTotal: 5, familyAllowance: 12, familyPriceMonthly: 30, familyRetailPriceMonthly: 40, sstRate: 8, currency: "MYR", maxUses: 1, uses: 1, expiresAt: null, createdAt: ago(12) },
    { code: "ZOO-B21E0D77", label: "Dr. Aina Rahman", kind: "individual", seatsTotal: 1, familyAllowance: 5, familyPriceMonthly: 30, familyRetailPriceMonthly: 45, sstRate: 8, currency: "MYR", maxUses: 1, uses: 1, expiresAt: null, createdAt: ago(97) },
    { code: "ZOO-4C8810EA", label: "StarSewa partnership", kind: "organisation", seatsTotal: 10, familyAllowance: 50, familyPriceMonthly: 0, familyRetailPriceMonthly: 0, sstRate: 0, currency: "MYR", maxUses: 1, uses: 1, expiresAt: null, createdAt: ago(182) },
  ];

  const team: TeamMember[] = [
    { accountId: "acc-awais", email: "awais@zooylo.app", role: "owner", addedAt: ago(400) },
    { accountId: "acc-hafiz", email: "hafiz@zooylo.app", role: "sales", addedAt: ago(200) },
    { accountId: "acc-lina", email: "lina@zooylo.app", role: "finance", addedAt: ago(150) },
    { accountId: "acc-sam", email: "sam@zooylo.app", role: "support", addedAt: ago(40) },
  ];

  const audit: DemoState["audit"] = [
    { id: "au-1", orgId: "org-willow", actorEmail: "hafiz@zooylo.app", entity: "sales_account", entityId: "org-willow", action: "update", before: { contact_phone: null, sales_owner: "Awais" }, after: { contact_phone: "+60 12-345 6789", sales_owner: "Hafiz" }, at: ago(20) },
    { id: "au-2", orgId: "org-willow", actorEmail: "awais@zooylo.app", entity: "license", entityId: "lic-willow", action: "update", before: { family_allowance: 15, contract_end: agoDate(-60) }, after: { family_allowance: 20, contract_end: ahead(125) }, at: ago(60) },
    { id: "au-3", orgId: "org-sunrise", actorEmail: "lina@zooylo.app", entity: "organisation", entityId: "org-sunrise", action: "update", before: { status: "active" }, after: { status: "suspended" }, at: ago(6) },
    { id: "au-4", orgId: "org-brightsteps", actorEmail: "lina@zooylo.app", entity: "invoice", entityId: "inv-5", action: "update", before: { status: "issued", paid_at: null }, after: { status: "paid", paid_at: ago(70) }, at: ago(70) },
    { id: "au-5", orgId: "org-kiddo", actorEmail: null, entity: "license", entityId: "lic-kiddo", action: "insert", before: null, after: { family_allowance: 12, family_price_monthly: 30, sst_rate: 8, currency: "MYR" }, at: ago(9) },
  ];

  const onboarding: Record<string, Onboarding> = {};
  for (const a of accounts) {
    const old = a.orgId !== "org-kiddo";
    const created = new Date(a.createdAt).getTime();
    const after = (d: number) => iso(created + d * DAY);
    onboarding[a.orgId] = {
      org_created: a.createdAt,
      admin_signed_in: after(1),
      first_staff_invited: old ? after(3) : null,
      first_licence: after(old ? 6 : 2),
      first_family_signed_in: old && a.licencesClaimed > 0 ? after(9) : null,
      first_child_shared: old && a.orgId !== "org-aina" ? after(14) : null,
      first_invoice: invoices.find((i) => i.orgId === a.orgId && i.status !== "draft")?.issuedAt ?? null,
    };
  }

  return { accounts, prospects, invoices, codes, team, audit, onboarding };
}

// ---- The source ------------------------------------------------------------------------

const ACCOUNT_KEYS: Record<string, keyof SalesAccount> = {
  legal_name: "legalName",
  registration_no: "registrationNo",
  country: "country",
  billing_address: "billingAddress",
  tax_id: "taxId",
  contact_name: "contactName",
  contact_email: "contactEmail",
  contact_phone: "contactPhone",
  sales_owner: "salesOwner",
  source: "source",
  notes: "notes",
  payment_terms_days: "paymentTermsDays",
  agreement_notes: "agreementNotes",
};

const PROSPECT_KEYS: Record<string, keyof Prospect> = {
  name: "name",
  kind: "kind",
  contact_name: "contactName",
  contact_email: "contactEmail",
  contact_phone: "contactPhone",
  stage: "stage",
  expected_licences: "expectedLicences",
  next_action: "nextAction",
  next_action_on: "nextActionOn",
  lost_reason: "lostReason",
  notes: "notes",
  org_id: "orgId",
};

const clean = (v: unknown) => (typeof v === "string" ? (v.trim() === "" ? null : v.trim()) : v ?? null);

export function createDemoSalesDataSource(nowMs: number = Date.now()): SalesDataSource {
  const s = seed(nowMs);
  const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;
  const findAccount = (orgId: string) => {
    const a = s.accounts.find((x) => x.orgId === orgId);
    if (!a) throw new PortalWriteError("demo", "no such organisation", "P0002");
    return a;
  };
  const record = (orgId: string | null, entity: string, entityId: string, action: string, before: Record<string, unknown> | null, after: Record<string, unknown> | null) => {
    s.audit.unshift({ id: newId("au"), orgId, actorEmail: s.team.find((m) => m.role === demoRole)?.email ?? "awais@zooylo.app", entity, entityId, action, before, after, at: iso(Date.now()) });
  };
  const outstanding = (orgId: string) =>
    round2(s.invoices.filter((i) => i.orgId === orgId && i.status === "issued").reduce((t, i) => t + i.amount, 0));

  function preview(month: string): BillingPreviewRow[] {
    const { start, end } = monthBounds(month);
    return s.accounts
      .filter((a) => a.familyPriceMonthly > 0)
      .map((a) => {
        const lm = new Date(a.createdAt).toISOString().slice(0, 10) <= start && a.status !== "closed" ? a.licencesClaimed : 0;
        const subtotal = round2(lm * a.familyPriceMonthly);
        const sst = round2((subtotal * a.sstRate) / 100);
        return {
          orgId: a.orgId,
          orgName: a.name,
          status: a.status,
          licenceMonths: lm,
          unitPrice: a.familyPriceMonthly,
          subtotal,
          sstAmount: sst,
          amount: round2(subtotal + sst),
          currency: a.currency,
          alreadyInvoiced: s.invoices.some((i) => i.orgId === a.orgId && i.status !== "void" && i.periodStart === start && i.periodEnd === end),
        };
      })
      .sort((x, y) => x.orgName.localeCompare(y.orgName));
  }

  function issue(orgId: string, periodStart: string, periodEnd: string, amount: number | null, due: string | null, note: string | null): string {
    const a = findAccount(orgId);
    const startMs = Date.parse(`${periodStart}T00:00:00Z`);
    const endMs = Date.parse(`${periodEnd}T00:00:00Z`);
    if (endMs < startMs) throw new PortalWriteError("demo", "period ends before it starts", "P0001");
    const months = Math.max((endMs - startMs + DAY) / (30 * DAY), 0);
    const lm = round2(a.licencesClaimed * Math.min(months, 12));
    const subtotal = round2(amount ?? lm * a.familyPriceMonthly);
    const sst = round2((subtotal * a.sstRate) / 100);
    const id = newId("inv");
    const inv: Invoice = {
      id,
      orgId,
      number: `INV-${new Date().toISOString().slice(0, 7).replace("-", "")}-${id.slice(-3).toUpperCase().padStart(3, "0")}`,
      periodStart,
      periodEnd,
      licenceMonths: lm,
      unitPrice: a.familyPriceMonthly,
      subtotal,
      sstRate: a.sstRate,
      sstAmount: sst,
      amount: round2(subtotal + sst),
      currency: a.currency,
      status: "issued",
      issuedAt: iso(Date.now()),
      dueAt: due,
      paidAt: null,
      note,
    };
    s.invoices.unshift(inv);
    a.outstanding = outstanding(orgId);
    if (!s.onboarding[orgId]?.first_invoice && s.onboarding[orgId]) s.onboarding[orgId].first_invoice = inv.issuedAt;
    record(orgId, "invoice", id, "insert", null, { number: inv.number, amount: inv.amount, status: "issued" });
    return id;
  }

  return {
    me: async () => ({ role: demoRole, email: s.team.find((m) => m.role === demoRole)?.email ?? "demo@zooylo.app" }),
    accounts: async () => clone(s.accounts),
    saveAccount: async (orgId, fields: AccountFields) => {
      must("accounts");
      const a = findAccount(orgId);
      if ("payment_terms_days" in fields || "agreement_notes" in fields) must("agreements");
      const before: Record<string, unknown> = {};
      const after: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(fields)) {
        const key = ACCOUNT_KEYS[k];
        if (!key) continue;
        const next = k === "country" ? String(clean(v) ?? "MY").toUpperCase() : k === "contact_email" && typeof v === "string" ? clean(v.toLowerCase()) : clean(v);
        if (JSON.stringify(a[key]) === JSON.stringify(next)) continue;
        before[k] = a[key];
        after[k] = next;
        (a as unknown as Record<string, unknown>)[key] = next;
      }
      if (Object.keys(after).length) record(orgId, "sales_account", orgId, "update", before, after);
    },
    prospects: async () => clone(s.prospects),
    saveProspect: async (id, fields: ProspectFields) => {
      must("pipeline");
      let p = id ? s.prospects.find((x) => x.id === id) : undefined;
      if (id && !p) throw new PortalWriteError("demo", "no such prospect", "P0002");
      if (!p && !String(fields.name ?? "").trim()) throw new PortalWriteError("demo", "a prospect needs a name", "P0001");
      if (fields.stage === "lost" && !String(fields.lost_reason ?? "").trim() && !p?.lostReason) {
        throw new PortalWriteError("demo", "say why it was lost", "P0001");
      }
      const before = p ? clone(p) : null;
      if (!p) {
        p = { id: newId("pr"), name: String(fields.name).trim(), kind: "organisation", contactName: null, contactEmail: null, contactPhone: null, stage: "lead", expectedLicences: null, nextAction: null, nextActionOn: null, lostReason: null, notes: null, orgId: null, createdAt: iso(Date.now()), updatedAt: iso(Date.now()) };
        s.prospects.unshift(p);
      }
      for (const [k, v] of Object.entries(fields)) {
        const key = PROSPECT_KEYS[k];
        if (!key) continue;
        if (k === "name" && !String(v ?? "").trim()) continue;
        (p as unknown as Record<string, unknown>)[key] = clean(v);
      }
      p.updatedAt = iso(Date.now());
      record(p.orgId, "sales_prospect", p.id, before ? "update" : "insert", before as Record<string, unknown> | null, clone(p) as unknown as Record<string, unknown>);
      return p.id;
    },
    onboarding: async (orgId) => clone(s.onboarding[orgId] ?? ({} as Onboarding)),
    receivables: async () => {
      const today = Date.parse(`${isoDate(Date.now())}T00:00:00Z`);
      const out: Receivable[] = s.invoices
        .filter((i) => i.status === "issued")
        .map((i) => {
          const org = s.accounts.find((a) => a.orgId === i.orgId);
          const due = i.dueAt ?? isoDate(Date.parse(i.issuedAt) + (org?.paymentTermsDays ?? 30) * DAY);
          const days = Math.round((today - Date.parse(`${due}T00:00:00Z`)) / DAY);
          return {
            invoiceId: i.id,
            orgId: i.orgId,
            orgName: org?.name ?? "",
            number: i.number,
            amount: i.amount,
            currency: i.currency,
            issuedAt: i.issuedAt,
            dueAt: due,
            daysOverdue: Math.max(0, days),
            ageBucket: bucketFor(days, days <= 0),
          };
        });
      return out.sort((a, b) => b.daysOverdue - a.daysOverdue);
    },
    billingPreview: async (month) => preview(month),
    issueMonth: async (month) => {
      must("invoices");
      const { start, end, endMs } = monthBounds(month);
      if (endMs >= Date.parse(`${isoDate(Date.now())}T00:00:00Z`)) {
        throw new PortalWriteError("demo", "a month can be invoiced once it has ended", "P0001");
      }
      let n = 0;
      for (const r of preview(month)) {
        if (r.alreadyInvoiced || r.amount <= 0) continue;
        const a = findAccount(r.orgId);
        issue(r.orgId, start, end, r.subtotal, isoDate(Date.now() + a.paymentTermsDays * DAY), null);
        const inv = s.invoices[0];
        inv.licenceMonths = r.licenceMonths;
        n++;
      }
      return n;
    },
    reports: async (): Promise<SalesReports> => {
      const active = s.accounts.filter((a) => a.status === "active");
      const funnel = { lead: 0, qualified: 0, demo: 0, proposal: 0, negotiating: 0, won: 0, lost: 0 };
      for (const p of s.prospects) funnel[p.stage]++;
      const d = new Date();
      const months = Array.from({ length: 6 }, (_, k) => {
        const m = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 5 + k, 1));
        const ym = m.toISOString().slice(0, 7);
        const lm = s.accounts.reduce((t, a) => {
          if (a.createdAt.slice(0, 7) > ym) return t;
          const share = a.status === "closed" ? 0 : a.licencesClaimed;
          return t + (k === 5 ? round2(share * (d.getUTCDate() / 30)) : share * (0.8 + k * 0.04));
        }, 0);
        return { month: ym, licenceMonths: round2(lm) };
      });
      return {
        currency: "MYR",
        mrr: round2(active.reduce((t, a) => t + a.familyPriceMonthly * a.licencesClaimed, 0)),
        organisationsActive: active.length,
        organisationsSuspended: s.accounts.filter((a) => a.status === "suspended").length,
        licencesLive: s.accounts.reduce((t, a) => t + a.licencesLive, 0),
        licencesClaimed: s.accounts.reduce((t, a) => t + a.licencesClaimed, 0),
        receivables: round2(s.invoices.filter((i) => i.status === "issued").reduce((t, i) => t + i.amount, 0)),
        paidLast90Days: round2(
          s.invoices.filter((i) => i.status === "paid" && i.paidAt && Date.parse(i.paidAt) > Date.now() - 90 * DAY).reduce((t, i) => t + i.amount, 0),
        ),
        licenceMonthsByMonth: months,
        funnel,
      };
    },
    auditLog: async (orgId) => clone(s.audit.filter((e) => orgId === null || e.orgId === orgId)).map(({ orgId: _o, ...e }) => e),
    team: async () => clone(s.team).sort((a, b) => a.email.localeCompare(b.email)),
    setRole: async (accountId, role) => {
      if (demoRole !== "owner") throw new PortalWriteError("demo", "not authorized: owners only", "42501");
      const m = s.team.find((x) => x.accountId === accountId);
      if (m?.role === "owner" && role !== "owner" && s.team.filter((x) => x.role === "owner").length <= 1) {
        throw new PortalWriteError("demo", "the last owner cannot be removed", "P0001");
      }
      if (!m) throw new PortalWriteError("demo", "no such account", "P0002");
      if (role === null) s.team = s.team.filter((x) => x.accountId !== accountId);
      else m.role = role;
    },
    addTeamMember: async (email, role) => {
      if (demoRole !== "owner") throw new PortalWriteError("demo", "not authorized: owners only", "42501");
      const e = email.trim().toLowerCase();
      // In the demo, only zooylo.app addresses "have signed in before".
      if (!e.endsWith("@zooylo.app")) throw new PortalWriteError("demo", "that person has not signed in to Zooylo yet", "P0002");
      const existing = s.team.find((x) => x.email === e);
      if (existing) {
        existing.role = role;
        return existing.accountId;
      }
      const id = newId("acc");
      s.team.push({ accountId: id, email: e, role, addedAt: iso(Date.now()) });
      return id;
    },
    accessCodes: async () => clone(s.codes),
    createAccessCode: async (input) => {
      must("codes");
      if (input.familyRetailPrice > 0 && input.familyPrice > input.familyRetailPrice) {
        throw new PortalWriteError("demo", "Zooylo's share cannot be more than what the family pays", "P0001");
      }
      const code = `ZOO-${Math.random().toString(16).slice(2, 10).toUpperCase().padEnd(8, "0")}`;
      s.codes.unshift({
        code,
        label: input.label,
        kind: input.kind as OrgKind,
        seatsTotal: Math.max(input.seats ?? (input.kind === "individual" ? 1 : 5), 1),
        familyAllowance: input.familyAllowance,
        familyPriceMonthly: input.familyPrice,
        familyRetailPriceMonthly: input.familyRetailPrice,
        sstRate: input.sstRate,
        currency: input.currency,
        maxUses: input.maxUses,
        uses: 0,
        expiresAt: input.expiresAt,
        createdAt: iso(Date.now()),
      });
      record(null, "org_access_code", code, "insert", null, { label: input.label, family_allowance: input.familyAllowance, family_price_monthly: input.familyPrice });
      return code;
    },
    setLicence: async (orgId, t) => {
      must("agreements");
      const a = findAccount(orgId);
      if (t.sstRate != null && (t.sstRate < 0 || t.sstRate > 100)) throw new PortalWriteError("demo", "invalid SST rate", "P0001");
      const before = { family_allowance: a.familyAllowance, family_price_monthly: a.familyPriceMonthly, family_retail_price_monthly: a.familyRetailPriceMonthly, sst_rate: a.sstRate, currency: a.currency, billing_email: a.billingEmail, contract_end: a.contractEnd };
      if (t.familyAllowance != null) a.familyAllowance = t.familyAllowance;
      if (t.familyPrice != null) a.familyPriceMonthly = t.familyPrice;
      if (t.familyRetailPrice != null) a.familyRetailPriceMonthly = t.familyRetailPrice;
      if (t.sstRate != null) a.sstRate = t.sstRate;
      if (t.currency) a.currency = t.currency.toUpperCase();
      if (t.billingEmail != null) a.billingEmail = t.billingEmail;
      if (t.contractEnd != null) a.contractEnd = t.contractEnd;
      const after = { family_allowance: a.familyAllowance, family_price_monthly: a.familyPriceMonthly, family_retail_price_monthly: a.familyRetailPriceMonthly, sst_rate: a.sstRate, currency: a.currency, billing_email: a.billingEmail, contract_end: a.contractEnd };
      if (JSON.stringify(before) !== JSON.stringify(after)) record(orgId, "license", `lic-${orgId}`, "update", before, after);
    },
    setOrgStatus: async (orgId, status, endLicences) => {
      must("org_status");
      const a = findAccount(orgId);
      const before = { status: a.status };
      a.status = status as OrgLifecycle;
      if (endLicences) {
        a.licencesLive = 0;
        a.licencesClaimed = 0;
      }
      record(orgId, "organisation", orgId, "update", before, { status });
    },
    invoices: async (orgId) => clone(s.invoices.filter((i) => i.orgId === orgId)).sort((a, b) => b.periodStart.localeCompare(a.periodStart)),
    previewInvoice: async (orgId, periodStart, periodEnd) => {
      const a = findAccount(orgId);
      const startMs = Date.parse(`${periodStart}T00:00:00Z`);
      const endMs = Date.parse(`${periodEnd}T00:00:00Z`);
      if (!(endMs >= startMs)) return null;
      const lm = round2(a.licencesClaimed * Math.min((endMs - startMs + DAY) / (30 * DAY), 12));
      const subtotal = round2(lm * a.familyPriceMonthly);
      const sst = round2((subtotal * a.sstRate) / 100);
      return { licenceMonths: lm, unitPrice: a.familyPriceMonthly, subtotal, sstRate: a.sstRate, sstAmount: sst, amount: round2(subtotal + sst), currency: a.currency };
    },
    issueInvoice: async (orgId, input) => {
      must("invoices");
      return issue(orgId, input.periodStart, input.periodEnd, input.amount, input.due, input.note);
    },
    setInvoiceStatus: async (invoiceId, status) => {
      must("invoices");
      const inv = s.invoices.find((i) => i.id === invoiceId);
      if (!inv) throw new PortalWriteError("demo", "no such invoice", "P0002");
      const before = { status: inv.status, paid_at: inv.paidAt };
      inv.status = status;
      inv.paidAt = status === "paid" ? iso(Date.now()) : null;
      const a = s.accounts.find((x) => x.orgId === inv.orgId);
      if (a) a.outstanding = outstanding(a.orgId);
      record(inv.orgId, "invoice", inv.id, "update", before, { status: inv.status, paid_at: inv.paidAt });
    },
  };
}
